import { MultiLineText } from '@/components/text/MultiLineText';
import { formatDateHHMM } from '@/utils/date-time/formatDateHHMM';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const MEMO_MAX_LENGTH = 200;

interface LicenseKeyMemoCellProps {
  memo?: string;
  memoIssuerName?: string;
  memoCreatedAt?: string;
  onSave: (content: string) => void;
}

const LicenseKeyMemoCell: React.FC<LicenseKeyMemoCellProps> = ({
  memo,
  memoIssuerName,
  memoCreatedAt,
  onSave,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(memo ?? '');
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const hasMemo = Boolean(memo);
  const trimmedLength = draft.trim().length;
  const isOverLimit = trimmedLength > MEMO_MAX_LENGTH;

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDraft(memo ?? '');
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, memo]);

  const handleOpen = () => {
    if (!hasMemo) setDraft('');
    setIsOpen(true);
  };

  const handleSave = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed.length > MEMO_MAX_LENGTH) return;
    onSave(trimmed);
    setIsOpen(false);
  };

  return (
    <Container ref={containerRef}>
      {hasMemo ? (
        <MemoPreviewButton type="button" onClick={handleOpen}>
          <LockIcon viewBox="0 0 16 16" fill="none">
            <rect
              x="3.5"
              y="7"
              width="9"
              height="6"
              rx="1.5"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </LockIcon>
          <PreviewText>{memo}</PreviewText>
        </MemoPreviewButton>
      ) : (
        <AddMemoButton type="button" onClick={handleOpen}>
          + {t('licenseManagement.memoCell.addMemo')}
        </AddMemoButton>
      )}

      {isOpen && hasMemo && (
        <Popover>
          <ReadOnlyContent>{memo}</ReadOnlyContent>
          <MemoMeta>
            {t('licenseManagement.memoCell.writtenBy')}: {memoIssuerName} ·{' '}
            {formatDateHHMM({ dateString: memoCreatedAt ?? '' })}
          </MemoMeta>
          <MemoNotice>
            {t('licenseManagement.memoCell.lockedNotice')}
          </MemoNotice>

          <PopoverFooter>
            <TextButton type="button" onClick={() => setIsOpen(false)}>
              {t('licenseManagement.memoCell.close')}
            </TextButton>
          </PopoverFooter>
        </Popover>
      )}

      {isOpen && !hasMemo && (
        <Popover>
          <PopoverTextarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('licenseManagement.memoCell.placeholder')}
            maxLength={MEMO_MAX_LENGTH}
            autoFocus
          />
          <MemoNotice>
            <MultiLineText
              translationKey={t('licenseManagement.memoCell.writeNotice')}
            />
          </MemoNotice>
          <CharCount $isOverLimit={isOverLimit}>
            {trimmedLength}/{MEMO_MAX_LENGTH}
          </CharCount>

          <PopoverFooter>
            <TextButton type="button" onClick={() => setIsOpen(false)}>
              {t('licenseManagement.memoCell.cancel')}
            </TextButton>
            <SaveButton
              type="button"
              onClick={handleSave}
              disabled={!draft.trim() || isOverLimit}
            >
              {t('licenseManagement.memoCell.register')}
            </SaveButton>
          </PopoverFooter>
        </Popover>
      )}
    </Container>
  );
};

export default LicenseKeyMemoCell;

const Container = styled.div`
  position: relative;
  display: inline-block;
`;

const AddMemoButton = styled.button`
  background: #ffffff;
  border: 1px dashed #93c5fd;
  border-radius: 999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #2563eb;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: #eff6ff;
  }
`;

const MemoPreviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: 180px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const LockIcon = styled.svg`
  width: 11px;
  height: 11px;
  color: #6b7280;
  flex-shrink: 0;
`;

const PreviewText = styled.span`
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 320px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
  padding: 14px;
  z-index: 20;
`;

const PopoverTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  max-height: 160px;
  resize: vertical;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px;
  font-size: 12px;
  color: #111827;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2563eb;
  }
`;

const ReadOnlyContent = styled.p`
  margin: 0;
  font-size: 12px;
  color: #111827;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 200px;
  overflow-y: auto;
`;

const MemoMeta = styled.p`
  margin: 8px 0 0;
  font-size: 11px;
  color: #9ca3af;
`;

const MemoNotice = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #d97706;
`;

const CounterRow = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const CharCount = styled.span<{ $isOverLimit: boolean }>`
  display: block;
  margin-top: 4px;
  text-align: right;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: ${({ $isOverLimit }) => ($isOverLimit ? '#dc2626' : '#9ca3af')};
`;

const PopoverFooter = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  padding: 6px 8px;

  &:hover {
    color: #374151;
  }
`;

const SaveButton = styled.button`
  background: #2563eb;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  cursor: pointer;

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;
